import { Modal, message } from 'antd';
import { clearToken, getToken, isLoading } from 'domain/reducers';
import { createBulletPointsMessage } from '../common';
import history from '../history';
import myHttpClient from '../httpClient';
import { IMailSendSettingMail } from '../model';
import { getState } from '../store';

/**
 * メール送信設定画面 リフレッシュ
 * @param keepRegistrationArea 登録エリアをそのままにするかどうか
 */
export const refreshAdminMailSendSetting = async (keepRegistrationArea = false): Promise<void> => {
  const currentState = getState();
  currentState.pageTitle = 'メール送信設定';

  try {
    // backend呼び出し
    const response: any = await myHttpClient.get('/admin/mail-send/setting');

    if (!keepRegistrationArea) {
      currentState.mailSendSetting.companyId = '';
      currentState.mailSendSetting.mailBody = '';
    }
    currentState.mailSendSetting.mailList = response.mailList;

    history.push('/admin/mail-send/setting');
  } catch (e) {
    history.push('/systemError');
  } finally {
    // ローディング中を解除
    isLoading(false);
  }
};

/**
 * メール送信設定画面 画面入力値の保存
 * @param fieldsValue 画面入力値
 * @param selectedRowKeys 登録メール一覧の選択行の企業ID
 */
export const saveAdminMailSendSettingParam = (fieldsValue: any, selectedRowKeys: string[] | number[]): void => {
  const currentState = getState();
  currentState.mailSendSetting.companyId = fieldsValue.companyId;
  currentState.mailSendSetting.mailBody = fieldsValue.mailBody;
  currentState.mailSendSetting.mailList.forEach(mail => {
    mail.validFlag = !selectedRowKeys.some(selectedRowKey => selectedRowKey === mail.companyId);
  });
};

/**
 * メール送信設定画面 登録確認メッセージ 「はい」ボタン押下処理
 */
export const okAdminMailSendSettingRegistration = async (): Promise<void> => {
  // ローディング中を設定
  isLoading(true);

  const currentState = getState();
  const { companyId, mailBody } = currentState.mailSendSetting;

  let doesRefresh: boolean;
  let keepRegistrationArea = true;
  try {
    // バックエンドを呼び出す
    const response: any = await myHttpClient.put('/admin/mail-send/setting', {
      companyId,
      mailBody
    });

    // 正常な場合
    if (response && response.message) {
      message.error(response.message, 3);
    } else {
      message.success('登録しました。', 3);
      keepRegistrationArea = false;
    }

    doesRefresh = true;
  } catch (e) {
    // エラーが発生した場合
    message.error('登録に失敗しました。', 3);
    doesRefresh = false;

    // システムエラー画面に遷移
    history.push('/systemError');
  } finally {
    // ローディング中を解除
    isLoading(false);
  }

  if (!doesRefresh) {
    return;
  }

  // 画面リフレッシュ
  isLoading(true);
  await refreshAdminMailSendSetting(keepRegistrationArea);
};

/**
 * メール送信設定画面 登録確認メッセージ 「いいえ」ボタン押下処理
 */
export const cancelAdminMailSendSettingRegistration = (): void => {
  // トークン削除
  clearToken().catch((error: any) => {
    // ignore
  });
};

/**
 * メール送信設定画面 登録確認メッセージ表示処理
 */
export const confirmAdminMailSendSettingRegistration = async (): Promise<void> => {
  try {
    // トークンの取得
    await getToken();

    // 確認メッセージの表示
    Modal.confirm({
      title: '登録します。よろしいですか？',
      className: 'registrationConfirmModal',
      centered: true,
      okText: 'はい',
      cancelText: 'いいえ',
      autoFocusButton: 'cancel',
      onOk: () => {
        /* istanbul ignore next */
        okAdminMailSendSettingRegistration();
      },
      onCancel: () => {
        /* istanbul ignore next */
        cancelAdminMailSendSettingRegistration();
      }
    });
  } catch (e) {
    // エラーが発生した場合
    message.error('登録に失敗しました。', 3);
  }
};

/**
 * メール送信設定画面 削除確認メッセージ 「はい」ボタン押下処理
 */
export const okAdminMailSendSettingDeletion = async (): Promise<void> => {
  // ローディング中を設定
  isLoading(true);

  const currentState = getState();
  const mailList: IMailSendSettingMail[] = currentState.mailSendSetting.mailList.filter(mail => !mail.validFlag);

  let doesRefresh: boolean;
  try {
    // バックエンドを呼び出す
    const response: any = await myHttpClient.delete('/admin/mail-send/setting', {
      data: {
        mailList
      }
    });

    // 正常な場合
    if (response && response.message) {
      const bulletPoints = response.errorCompanyList.map((company: any) => `${company.companyId}：${company.companyName}`);
      const content = createBulletPointsMessage(response.message, bulletPoints);
      message.error(content, 3);
    } else {
      message.success('削除しました。', 3);
    }

    doesRefresh = true;
  } catch (e) {
    // エラーが発生した場合
    message.error('削除に失敗しました。', 3);
    doesRefresh = false;

    // システムエラー画面に遷移
    history.push('/systemError');
  } finally {
    // ローディング中を解除
    isLoading(false);
  }

  if (!doesRefresh) {
    return;
  }

  // 画面リフレッシュ
  isLoading(true);
  await refreshAdminMailSendSetting(true);
};

/**
 * メール送信設定画面 削除確認メッセージ 「いいえ」ボタン押下処理
 */
export const cancelAdminMailSendSettingDeletion = (): void => {
  // トークン削除
  clearToken().catch((error: any) => {
    // ignore
  });
};

/**
 * メール送信設定画面 削除確認メッセージ表示処理
 * @param createModalContent メッセージコンテンツ生成処理
 */
export const confirmAdminMailSendSettingDeletion = async (
  createModalContent: (deletionMailList: IMailSendSettingMail[]) => React.ReactNode
): Promise<void> => {
  const currentState = getState();

  try {
    // トークンの取得
    await getToken();

    const deletionMailList: IMailSendSettingMail[] = currentState.mailSendSetting.mailList.filter(mail => !mail.validFlag);

    // 確認メッセージの表示
    Modal.confirm({
      title: '削除します。よろしいですか？',
      className: 'deletionConfirmModal',
      width: 600,
      centered: true,
      content: createModalContent(deletionMailList),
      okText: 'はい',
      cancelText: 'いいえ',
      autoFocusButton: 'cancel',
      onOk: () => {
        /* istanbul ignore next */
        okAdminMailSendSettingDeletion();
      },
      onCancel: () => {
        /* istanbul ignore next */
        cancelAdminMailSendSettingDeletion();
      }
    });
  } catch (e) {
    // エラーが発生した場合
    message.error('削除に失敗しました。', 3);
  }
};
